import React from "react";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import { breakTotals } from "./timecardBreakUtils";

const pickerSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px !important",
    height: "40px",
    width: "100%",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6d28d9",
    },
    backgroundColor: "white",
  },
};

// A picked time means that exact minute: drop seconds the picker would
// otherwise carry over, unseen, from the previous value.
const BREAK_TYPE_OPTIONS = [
  { value: false, label: "Unpaid" },
  { value: true, label: "Paid" },
];

function toMillis(value) {
  if (!value) return null;
  const ms = value.getTime();
  return Number.isNaN(ms) ? NaN : Math.floor(ms / 60000) * 60000;
}

// Edits individual breaks with the same pickers as the shift clock in/out.
// Every change goes through onChange with the full list; nothing is clamped.
export default function BreakEditor({ breaks, onChange, errors, shiftStart, shiftEnd, paidBreakTrackingEnabled }) {
  const list = breaks || [];
  const totals = breakTotals(list);

  function updateBreak(index, changes) {
    onChange(list.map((brk, i) => (i === index ? { ...brk, ...changes } : brk)));
  }

  function addBreak() {
    const hasShift = Number.isFinite(shiftStart) && Number.isFinite(shiftEnd) && shiftEnd > shiftStart;
    const minute = 60000;
    const start = hasShift
      ? Math.floor((shiftStart + (shiftEnd - shiftStart) / 2) / minute) * minute
      : Math.floor(Date.now() / minute) * minute;
    const end = hasShift ? Math.min(start + 30 * minute, shiftEnd) : start + 30 * minute;
    onChange([...list, { uuid: null, is_paid: false, clocked_in_at: start, clocked_out_at: end }]);
  }

  return (
    <div className="mt-5">
      <p className="text-sm text-slate-500">Breaks</p>
      {list.length === 0 && <p className="mt-1 text-sm text-slate-400">No breaks</p>}
      {list.map((brk, index) => (
        <div key={brk.uuid || `new-${index}`} className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between">
            {paidBreakTrackingEnabled ? (
              <div className="w-36">
                <label id={`break-${index}-type-label`} htmlFor={`break-${index}-type`} className="sr-only">
                  {`Break ${index + 1} type`}
                </label>
                <RightworkSelect
                  id={`break-${index}-type`}
                  value={Boolean(brk.is_paid)}
                  onChange={(event) => updateBreak(index, { is_paid: event.target.value })}
                  options={BREAK_TYPE_OPTIONS}
                />
              </div>
            ) : (
              <span className="text-sm text-slate-600">{brk.is_paid ? "Paid break" : "Break"}</span>
            )}
            <button
              type="button"
              aria-label={`Remove break ${index + 1}`}
              className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 hover:text-red-700"
              onClick={() => onChange(list.filter((_, i) => i !== index))}
            >
              <DeleteOutlineIcon style={{ fontSize: "18px" }} />
            </button>
          </div>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className="mt-2">
              <DateTimePicker
                views={["month", "day", "hours", "minutes"]}
                value={Number.isFinite(brk.clocked_in_at) ? new Date(brk.clocked_in_at) : null}
                onChange={(value) => updateBreak(index, { clocked_in_at: toMillis(value) })}
                slotProps={{ textField: { size: "small", fullWidth: true, inputProps: { "aria-label": `Break ${index + 1} start` }, sx: pickerSx } }}
              />
            </div>
            <div className="mt-2">
              <DateTimePicker
                views={["month", "day", "hours", "minutes"]}
                value={Number.isFinite(brk.clocked_out_at) ? new Date(brk.clocked_out_at) : null}
                onChange={(value) => updateBreak(index, { clocked_out_at: toMillis(value) })}
                slotProps={{ textField: { size: "small", fullWidth: true, inputProps: { "aria-label": `Break ${index + 1} end` }, sx: pickerSx } }}
              />
            </div>
          </LocalizationProvider>
          {errors?.[index] && <p role="alert" className="mt-1 text-xs text-red-700">{errors[index]}</p>}
        </div>
      ))}
      <button
        type="button"
        className="mt-2 flex items-center gap-1 text-sm font-medium text-violet-700 hover:text-violet-900"
        onClick={addBreak}
      >
        <AddIcon style={{ fontSize: "18px" }} /> Add break
      </button>
      <p className="mt-2 text-sm text-slate-600">
        {`Unpaid ${totals.unpaid} min`}
        {paidBreakTrackingEnabled && ` · Paid ${totals.paid} min`}
      </p>
    </div>
  );
}
